import { Button, IconHelpSupport } from "@studyplus/boron-ui";
import classNames from "classnames";
import { Form, Formik, type FormikHelpers } from "formik";
import { type FC, useEffect, useState } from "react";
import Select, {
  type ClearIndicatorProps,
  components as selectComponents,
} from "react-select";
import { ButtonSpinner } from "../../../components/atoms/Button/ButtonSpinner";
import DeleteIcon from "../../../components/atoms/DeleteIcon";
import Icon from "../../../components/atoms/Icon";
import { MainSection } from "../../../components/atoms/MainSection";
import type { DefaultPeriod } from "../../../components/features/AiAssistantTemplateForm/validationSchema";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverProvider,
  PopoverTrigger,
} from "../../../components/general/Popover/Popover";
import type { StudentWithStayInterface } from "../../../interfaces/StudentInterface";
import { useFetchAiAssistantTemplates } from "../../SectionsSettingsAiAssistantTemplatesPage/useFetchAiAssistantTemplates";
import { AiAssistantDateRangePicker } from "./AiAssistantDateRangePicker";
import {
  convertDefaultPeriodToDateRange,
  type DateRange,
  DEFAULT_INITIAL_PERIOD,
} from "./periodConverter";
import styles from "./styles.scss";
import {
  useFetchAiAssistantKarteGenerationStatus,
  useNotifyAiAssistantKarteGenerationResult,
} from "./useFetchAiAssistantKarteGenerationStatus";
import { useRequestAiAssistantKarteGeneration } from "./useRequestAiAssistantKarteGeneration";
import validationSchema, {
  type AiAssistantKarteGenerationFormValues,
} from "./validationSchema";

type Props = {
  student: StudentWithStayInterface;
};

const DESCRIPTION =
  "生徒の学習状況・面談記録を分析し、AIがテンプレートに従ってカルテを自動生成します。生成されたカルテは、状況把握や面談等にご活用ください。\n※AIが生成した内容は必ずご確認ください。必要に応じて編集してからご使用ください。";

const TEMPLATE_HELP_TEXT =
  "AIアシスタントテンプレートから指導方針や\n面談の目的に合わせた独自のプロンプトを\n作成・編集できます";
const PERIOD_HELP_TEXT = "設定期間内の学習データを\n分析してカルテを作成します";

type TemplateOption = {
  value: number;
  label: string;
  defaultPeriod: DefaultPeriod;
};

const TemplateClearIndicator = (props: ClearIndicatorProps<TemplateOption>) => (
  <selectComponents.ClearIndicator {...props}>
    <DeleteIcon theme="gray" size="small" />
  </selectComponents.ClearIndicator>
);

const HiddenIndicatorSeparator = () => null;

const StudentAiAssistantKarteField: FC<Props> = ({ student }) => {
  if (!student.aiAssistantAvailable) {
    return null;
  }
  return <StudentAiAssistantKarteFieldInner student={student} />;
};

const StudentAiAssistantKarteFieldInner: FC<Props> = ({ student }) => {
  const sectionId = student.section.id;

  const { data: templates } = useFetchAiAssistantTemplates({ sectionId });
  const templateOptions: TemplateOption[] = (templates ?? []).map(
    (template) => ({
      value: template.id,
      label: template.name,
      defaultPeriod: template.defaultPeriod,
    }),
  );

  const { mutate: requestGeneration, isPending } =
    useRequestAiAssistantKarteGeneration({ studentId: student.id });

  // GEN-08/GEN-09: 進行状態のポーリング。初回応答前・照会エラー時はボタンをグレー非活性にする
  const { data: generationStatus, isError: hasGenerationStatusError } =
    useFetchAiAssistantKarteGenerationStatus({ studentId: student.id });

  // GEN-10/GEN-11: 完了・失敗の遷移検知によるフラッシュ表示とカルテ一覧のリフレッシュ
  useNotifyAiAssistantKarteGenerationResult({
    studentId: student.id,
    status: generationStatus?.status,
  });

  // GEN-04/GEN-09: 生成リクエスト成功（201）後、ポーリング応答に生成が反映されるまでの間も
  // 「生成中…」表示を維持する（stale な acceptable: true による再活性・連打を防ぐ）。
  // POST 後の初回ポーリングで確定ステータス（pending/completed/failed）を観測したら解除する
  const [isAwaitingGenerationStart, setIsAwaitingGenerationStart] =
    useState(false);
  useEffect(() => {
    if (generationStatus?.status != null) {
      setIsAwaitingGenerationStart(false);
    }
  }, [generationStatus?.status]);

  const isGenerating =
    isPending ||
    isAwaitingGenerationStart ||
    (!hasGenerationStatusError && generationStatus?.status === "pending");
  const isAcceptable =
    !hasGenerationStatusError && generationStatus?.acceptable === true;

  const initialPeriod = convertDefaultPeriodToDateRange(
    DEFAULT_INITIAL_PERIOD,
    new Date(),
  );
  const initialValues: AiAssistantKarteGenerationFormValues = {
    aiAssistantTemplateId: null,
    startDate: initialPeriod.startDate,
    endDate: initialPeriod.endDate,
  };

  const handleSubmit = (
    values: AiAssistantKarteGenerationFormValues,
    helpers: FormikHelpers<AiAssistantKarteGenerationFormValues>,
  ) => {
    requestGeneration(values, {
      onSuccess: () => {
        setIsAwaitingGenerationStart(true);
      },
      onSettled: () => {
        helpers.setSubmitting(false);
      },
    });
  };

  const templateSettingsUrl = `/sections/${sectionId}/settings/ai_assistant_templates`;

  return (
    <div className={styles.root} aria-label="AIアシスタントフィールド">
      <MainSection className={styles.mainSection}>
        <header className={styles.header}>
          <h2 className={styles.title}>
            <Icon name="icon-sparkles" className={styles.sparkles} />
            AIアシスタント
          </h2>
          <a href={templateSettingsUrl} className={styles.settingsLink}>
            AIアシスタントテンプレート設定
          </a>
        </header>
        <p className={styles.description}>{DESCRIPTION}</p>

        <Formik<AiAssistantKarteGenerationFormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => {
            // GEN-09: 活性は「受付可能かつ入力が妥当」の場合のみ。
            // 初回応答前・照会エラー時（isAcceptable=false）と生成中はいずれも非活性
            const disabled = !formik.isValid || !isAcceptable || isGenerating;

            const handleDateRangeChange = (range: DateRange) => {
              formik.setValues({
                ...formik.values,
                startDate: range.startDate,
                endDate: range.endDate,
              });
            };

            const selectedOption =
              templateOptions.find(
                (option) =>
                  option.value === formik.values.aiAssistantTemplateId,
              ) ?? null;

            return (
              <Form className={styles.form}>
                <div className={classNames(styles.field, styles.templateField)}>
                  <div className={styles.labelRow}>
                    <label
                      htmlFor="StudentAiAssistantKarteField-template"
                      className={styles.label}
                    >
                      AIアシスタントテンプレート
                    </label>
                    <PopoverProvider>
                      <Popover>
                        <PopoverTrigger>
                          <IconHelpSupport className="h-[1.4rem] w-[1.4rem] text-gray-800" />
                        </PopoverTrigger>
                        <PopoverPortal>
                          <PopoverContent side="top">
                            <div className="whitespace-pre-line text-center">
                              {TEMPLATE_HELP_TEXT}
                            </div>
                          </PopoverContent>
                        </PopoverPortal>
                      </Popover>
                    </PopoverProvider>
                  </div>
                  <Select<TemplateOption>
                    inputId="StudentAiAssistantKarteField-template"
                    options={templateOptions}
                    isClearable
                    placeholder="テンプレートを選択"
                    noOptionsMessage={() =>
                      "AIアシスタントテンプレートを登録することで設定できます"
                    }
                    value={selectedOption}
                    components={{
                      ClearIndicator: TemplateClearIndicator,
                      IndicatorSeparator: HiddenIndicatorSeparator,
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "4.4rem",
                        height: "4.4rem",
                      }),
                    }}
                    onChange={(option) => {
                      const nextValues: AiAssistantKarteGenerationFormValues = {
                        ...formik.values,
                        aiAssistantTemplateId: option?.value ?? null,
                      };
                      if (option) {
                        const range = convertDefaultPeriodToDateRange(
                          option.defaultPeriod,
                          new Date(),
                        );
                        nextValues.startDate = range.startDate;
                        nextValues.endDate = range.endDate;
                      }
                      formik.setValues(nextValues);
                    }}
                  />
                </div>

                <div className={classNames(styles.field, styles.periodField)}>
                  <div className={styles.labelRow}>
                    <span className={styles.label}>データ参照期間</span>
                    <PopoverProvider>
                      <Popover>
                        <PopoverTrigger>
                          <IconHelpSupport className="h-[1.4rem] w-[1.4rem] text-gray-800" />
                        </PopoverTrigger>
                        <PopoverPortal>
                          <PopoverContent side="top">
                            <div className="whitespace-pre-line text-center">
                              {PERIOD_HELP_TEXT}
                            </div>
                          </PopoverContent>
                        </PopoverPortal>
                      </Popover>
                    </PopoverProvider>
                  </div>
                  <AiAssistantDateRangePicker
                    dateRange={{
                      startDate: formik.values.startDate,
                      endDate: formik.values.endDate,
                    }}
                    onChangeDateRange={handleDateRangeChange}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={disabled}
                  className={styles.submitButton}
                >
                  {isGenerating ? (
                    <>
                      <span className={styles.submitSpinner}>
                        <ButtonSpinner />
                      </span>
                      生成中…
                    </>
                  ) : (
                    <>
                      <Icon
                        name="icon-sparkles"
                        className={styles.submitIcon}
                      />
                      AIアシスタントでカルテを生成
                    </>
                  )}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </MainSection>
    </div>
  );
};

export default StudentAiAssistantKarteField;
